// src/sim/bot/perception.ts — Bot algısı (DESIGN §12, ARCHITECTURE §8). Tahsissiz: her think'te `BotPerception` alanlarına düz sayı yazar.
// Hile yok: yalnız dünyanın herkese açık durumu (konum, hız, durum, hamle fazı) okunur; menzil/koni bilgisi brain'de InputFrame'e döner.
// Tehdit = rakibin `phase == Windup` (whoosh_light/heavy'nin sim eşdeğeri; ARCHITECTURE §8) — olay havuzu bot çağrısından önce
// temizlendiği için (game.ts: presenter.consume → events.clear) durum yoklaması kullanılır; Active faz da "hâlâ tehdit" sayılır (threatTicks 0).
// three import ETMEZ.
import { FState, MovePhase } from '../../core/types.ts';
import type { Fighter, Perception, SimWorld, Vec3 } from '../../core/types.ts';
import { v3, yawDelta, yawOf } from '../../core/vec3.ts';
import { NO_ID, NO_TICK } from '../../config/constants.ts';
import { T } from '../../config/tuning.ts';

/**
 * WP5 yerel genişletme: `Perception` (types.ts) düz sayıları yeterli değil — yön vektörleri, yatan rakip, tehdit türü ve kenar
 * normalleri de gerekiyor. types.ts yalnız WP0 değiştirdiği için burada `extends` ile tanımlanır (rapor: needsWP0).
 * Tüm alanlar her think'te yeniden yazılır; "yok" = NO_ID / NO_TICK / 0.
 */
export interface BotPerception extends Perception {
  /** Hedefin konumu ve bana göre birim yönü (XZ); yaklaşıyor mu (hızının bana doğru bileşeni > 0); ayak yüksekliği farkı. */
  targetX: number; targetZ: number; dirX: number; dirZ: number; targetApproaching: boolean; targetDy: number;
  /** Hedefin kenar dış normali (fırlatma yönü / kenar konumu için), tumble/launch durumu ve hızının o normal boyunca dış bileşeni
   *  (u/s; > 0 → kenara doğru uçuyor: DESIGN §12 fırsat (d) "kenar konumu"). */
  targetEdgeNx: number; targetEdgeNz: number; targetTumbling: boolean; targetValid: boolean; targetVelOut: number;
  /** En yakın canlı düşman (hedeften bağımsız; yerde davranış ve eşya kararları bunu kullanır). */
  nearestId: number; nearestDist: number; nearestDirX: number; nearestDirZ: number; nearestApproaching: boolean;
  /** Tehdit ayrıntısı: ağır mı, tutma mı, hamle başlangıç tick'i (aynı tehdide bir kez karar için anahtar), tehdidin bana yönü. */
  threatHeavy: boolean; threatGrab: boolean; threatStartTick: number; threatDirX: number; threatDirZ: number;
  /** En yakın YATAN düşman (fırsat 3: kaldır-taşı-fırlat / Kick). */
  lyingId: number; lyingDist: number; lyingDirX: number; lyingDirZ: number; lyingEdgeDist: number; lyingEdgeNx: number; lyingEdgeNz: number;
  /** Yatanın `lyingUntil` tick'i: knockdown başına tek (Lying↔Crawl geçişlerinde değişmez) → kaldırma zarı knockdown başına bir kez. */
  lyingKey: number;
  /** En yakın yerdeki eşyanın konumu ve yönü. */
  itemX: number; itemZ: number; itemDirX: number; itemDirZ: number;
  /** Son think penceresinde ring-out olan bir düşman (taunt tetiği; −1 yok). */
  fellId: number;
  /** Arena merkezine birim yön ve mesafe (DI / toparlanma). */
  centerDirX: number; centerDirZ: number; centerDist: number;
}

export function createPerception(): BotPerception {
  return {
    targetId: NO_ID, targetDist: Infinity, targetAngle: 0, targetPhase: MovePhase.None, targetHeavy: false, targetStun: 0,
    targetLying: false, targetAir: false, targetBlocking: false, targetEdgeDist: Infinity,
    threatId: NO_ID, threatTicks: 0,
    myEdgeDist: Infinity, myEdgeNx: 0, myEdgeNz: 0, outside: false,
    itemId: NO_ID, itemDist: Infinity, lastAttackerId: NO_ID,
    targetX: 0, targetZ: 0, dirX: 0, dirZ: 1, targetApproaching: false, targetDy: 0,
    targetEdgeNx: 0, targetEdgeNz: 0, targetTumbling: false, targetValid: false, targetVelOut: 0,
    nearestId: NO_ID, nearestDist: Infinity, nearestDirX: 0, nearestDirZ: 1, nearestApproaching: false,
    threatHeavy: false, threatGrab: false, threatStartTick: NO_TICK, threatDirX: 0, threatDirZ: 1,
    lyingId: NO_ID, lyingDist: Infinity, lyingDirX: 0, lyingDirZ: 1, lyingEdgeDist: Infinity, lyingEdgeNx: 0, lyingEdgeNz: 0,
    lyingKey: NO_TICK,
    itemX: 0, itemZ: 0, itemDirX: 0, itemDirZ: 1,
    fellId: NO_ID,
    centerDirX: 0, centerDirZ: 1, centerDist: 0,
  };
}

/** Hedef/tehdit olabilecek düşman: elenmiş, düşmekte ya da yeniden doğuş bekleyen dövüşçü sayılmaz. */
export function isEnemyAlive(e: Fighter): boolean {
  return e.state !== FState.Eliminated && e.state !== FState.Respawning && e.state !== FState.Falling && e.lives > 0;
}
/** Yatan (Lying / LyingFront) — kaldırılabilir, Kick'lenebilir. */
export const isLying = (e: Fighter): boolean => e.state === FState.Lying;
/** Havada ya da kontrolsüz uçuşta. */
export const isAirborne = (e: Fighter): boolean =>
  e.state === FState.Air || e.state === FState.AirDodge || e.state === FState.Launched || e.state === FState.Tumble || !e.grounded;

const tmpN: Vec3 = v3();   // edgeDist dış normali için scratch (tahsissiz)

/** Rakibin hızının bana doğru bileşeni > 0 → yaklaşıyor. dirX/dirZ = benden ona birim yön. */
function approaching(e: Fighter, dirX: number, dirZ: number): boolean {
  return e.vel.x * -dirX + e.vel.z * -dirZ > 0.2;   // 0.2 u/s: duran rakip "yaklaşmıyor" (gürültü eşiği)
}

/**
 * Algıyı doldurur. `targetId` brain'in seçtiği hedef (geçersizse en yakın düşman kullanılır; `targetValid` false).
 * Tek geçişte düşmanları tarar: en yakın, en yakın yatan, en erken tehdit, düşen; sonra hedef ve eşya ayrıntıları.
 */
export function perceive(world: SimWorld, self: Fighter, targetId: number, out: BotPerception): void {
  const tick = world.tick;
  const fighters = world.fighters;
  const px = self.pos.x, pz = self.pos.z;

  // ── kendi kenar durumum ──
  out.myEdgeDist = world.query.edgeDist(px, pz, tmpN);
  out.myEdgeNx = tmpN.x; out.myEdgeNz = tmpN.z;
  out.outside = !world.query.isInside(px, pz);
  const cx = world.arena.center.x - px, cz = world.arena.center.z - pz;
  out.centerDist = Math.hypot(cx, cz);
  if (out.centerDist > 1e-6) { out.centerDirX = cx / out.centerDist; out.centerDirZ = cz / out.centerDist; }
  else { out.centerDirX = 0; out.centerDirZ = 1; }
  out.lastAttackerId = self.lastHitBy;

  // ── düşman taraması ──
  out.nearestId = NO_ID; out.nearestDist = Infinity; out.nearestApproaching = false;
  out.lyingId = NO_ID; out.lyingDist = Infinity; out.lyingKey = NO_TICK;
  out.threatId = NO_ID; out.threatTicks = 0; out.threatHeavy = false; out.threatGrab = false; out.threatStartTick = NO_TICK;
  out.fellId = NO_ID;
  let bestThreatTicks = Infinity;
  for (let i = 0; i < fighters.length; i++) {
    const e = fighters[i];
    if (!e || e.id === self.id) continue;
    // ring-out olan (taunt): düşme tick'i son think penceresinde
    if (e.fellAt !== NO_TICK && tick - e.fellAt < T.BOT.thinkTicks && tick >= e.fellAt) out.fellId = e.id;
    if (!isEnemyAlive(e)) continue;
    const dx = e.pos.x - px, dz = e.pos.z - pz;
    const d = Math.hypot(dx, dz);
    const inv = d > 1e-6 ? 1 / d : 0;
    const ux = d > 1e-6 ? dx * inv : 0, uz = d > 1e-6 ? dz * inv : 1;
    if (d < out.nearestDist) {
      out.nearestDist = d; out.nearestId = e.id; out.nearestDirX = ux; out.nearestDirZ = uz;
      out.nearestApproaching = approaching(e, ux, uz);
    }
    if (isLying(e) && d < out.lyingDist) {
      out.lyingDist = d; out.lyingId = e.id; out.lyingDirX = ux; out.lyingDirZ = uz; out.lyingKey = e.lyingUntil;
      out.lyingEdgeDist = world.query.edgeDist(e.pos.x, e.pos.z, tmpN);
      out.lyingEdgeNx = tmpN.x; out.lyingEdgeNz = tmpN.z;
    }
    // tehdit: threatRange içinde windup (ya da hâlâ Active) — en erken darbe kazanır
    if (d <= T.BOT.threatRange && (e.state === FState.Attack || e.state === FState.GrabAttempt) &&
        (e.phase === MovePhase.Windup || e.phase === MovePhase.Active)) {
      const m = world.moves[e.moveIdx];
      const windup = m ? m.windup : 0;
      const tt = e.phase === MovePhase.Windup ? Math.max(0, windup - e.moveTick) : 0;
      if (tt < bestThreatTicks) {
        bestThreatTicks = tt;
        out.threatId = e.id; out.threatTicks = tt;
        out.threatHeavy = m ? m.heavy : false;
        out.threatGrab = e.state === FState.GrabAttempt || (m ? m.family === 'grab' : false);
        out.threatStartTick = tick - e.moveTick;
        out.threatDirX = ux; out.threatDirZ = uz;
      }
    }
  }

  // ── hedef ayrıntısı (geçersiz hedef → en yakın) ──
  let tgt = targetId !== NO_ID ? fighters[targetId] : undefined;
  out.targetValid = !!tgt && tgt.id !== self.id && isEnemyAlive(tgt);
  if (!out.targetValid) tgt = out.nearestId !== NO_ID ? fighters[out.nearestId] : undefined;
  if (tgt && tgt.id !== self.id) {
    const dx = tgt.pos.x - px, dz = tgt.pos.z - pz;
    const d = Math.hypot(dx, dz);
    out.targetId = tgt.id; out.targetDist = d;
    out.targetX = tgt.pos.x; out.targetZ = tgt.pos.z;
    if (d > 1e-6) { out.dirX = dx / d; out.dirZ = dz / d; } else { out.dirX = 0; out.dirZ = 1; }
    out.targetAngle = yawDelta(self.yaw, yawOf(dx, dz));
    out.targetApproaching = approaching(tgt, out.dirX, out.dirZ);
    out.targetDy = tgt.pos.y - self.pos.y;
    const m = tgt.state === FState.Attack ? world.moves[tgt.moveIdx] : undefined;
    out.targetPhase = tgt.state === FState.Attack ? tgt.phase : MovePhase.None;
    out.targetHeavy = m ? m.heavy : false;
    out.targetStun = tgt.stun;
    out.targetLying = isLying(tgt);
    out.targetAir = isAirborne(tgt);
    out.targetBlocking = tgt.blocking || tgt.state === FState.Block;
    out.targetTumbling = tgt.state === FState.Tumble || tgt.state === FState.Launched;
    out.targetEdgeDist = world.query.edgeDist(tgt.pos.x, tgt.pos.z, tmpN);
    out.targetEdgeNx = tmpN.x; out.targetEdgeNz = tmpN.z;
    out.targetVelOut = tgt.vel.x * tmpN.x + tgt.vel.z * tmpN.z;
  } else {
    out.targetId = NO_ID; out.targetDist = Infinity; out.targetAngle = 0; out.targetPhase = MovePhase.None;
    out.targetHeavy = false; out.targetStun = 0; out.targetLying = false; out.targetAir = false; out.targetBlocking = false;
    out.targetTumbling = false; out.targetEdgeDist = Infinity; out.targetApproaching = false; out.targetDy = 0;
    out.targetX = px; out.targetZ = pz; out.dirX = 0; out.dirZ = 1; out.targetEdgeNx = 0; out.targetEdgeNz = 0; out.targetVelOut = 0;
  }

  // ── en yakın alınabilir eşya (yerde ve alınabilir) ──
  out.itemId = NO_ID; out.itemDist = Infinity;
  const items = world.items;
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (!it || it.state !== 'ground' || it.pickableAt > tick) continue;
    const dx = it.pos.x - px, dz = it.pos.z - pz;
    const d = Math.hypot(dx, dz);
    if (d < out.itemDist) {
      out.itemDist = d; out.itemId = it.id; out.itemX = it.pos.x; out.itemZ = it.pos.z;
      if (d > 1e-6) { out.itemDirX = dx / d; out.itemDirZ = dz / d; } else { out.itemDirX = 0; out.itemDirZ = 1; }
    }
  }
}
